<script lang="ts" setup>
import type { BlockSerializedChild, BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import type { BlockInheritSlots } from '#layers/block-engine/types/block-editor';
import { until, useElementBounding } from '@vueuse/core';
import { computed, onBeforeUnmount, provide, shallowRef, useId, watch } from 'vue';
import { useBlockEditorLibrary } from '#layers/block-engine/composables/block-editor/library';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { blockChildKey } from '#layers/block-engine/utils/block/api/context';
import { useBlockSlots, useHorizontalLayout } from '#layers/block-engine/utils/block/api/helpers';
import { blockRefKey } from '#layers/block-engine/utils/block/const';
import { isBlockSerialized, provideInBlockEditor } from '#layers/block-engine/utils/block/editor';
import { getBlockRootElement } from '#layers/block-engine/utils/block/element';
import { useBlockRenderProps } from '#layers/block-engine/utils/block/props';
import { useHandleBlockClick } from '#layers/block-engine/utils/block/selection';

import { useBlockEditorChildBoundsElement } from '../../utils/block/api/editor-bounds';

const props = defineProps<{
	child: BlockSerializedChild;
	slotName?: string;
	index?: number;
}>();

defineSlots<BlockInheritSlots>();

const { block: editedBlock, variables, insertion, rootContext, bounds, selection, getParent, drag } = useBlockEditorContext();
const blockLibraryStore = useBlockEditorLibrary();
const horizontal = useHorizontalLayout();

// Retrieve block

const block = computed(() => blockLibraryStore.getBlock(props.child.blockId));

if (!editedBlock.value) {
	throw new Error('editedBlock is not defined');
}

// Block Props

const {
	getProps,
} = useBlockRenderProps({
	variableIdToName: variables.variableIdToName,
	variables: variables.variableValues,
	getRootContext: rootContext.getRootContextValues,
});

const propValues = computed(() => getProps({
	child: props.child,
	childBlock: block.value,
}));

// Slots

const blockSlots = useBlockSlots(block, () => props.child, propValues);

const childrenBySlot = computed(() => blockSlots.value.reduce((obj, slot) => {
	obj[slot.name] = (props.child.slots[slot.name] ?? []).map((childId) => editedBlock.value?.children?.[childId]).filter((c) => !!c) as Array<BlockSerializedChild | BlockSerializedSlotOutlet>;
	return obj;
}, {} as Record<string, Array<BlockSerializedChild | BlockSerializedSlotOutlet>>));

// Selection

const selected = computed(() => selection.selectedChildIds.value.includes(props.child.id));
const hiddenByCanvasDrag = computed(() => {
	return drag.draggedBlock.value?.source === 'canvas' && drag.draggedBlock.value.childId === props.child.id;
});
const draggedCanvasPlaceholderStyle = computed(() => {
	const draggedBounds = hiddenByCanvasDrag.value ? drag.draggedBlock.value?.bounds : undefined;

	if (!draggedBounds) {
		return;
	}

	return {
		width: `${draggedBounds.width}px`,
		height: `${draggedBounds.height}px`,
		flexShrink: 0,
	};
});

function isInsertionTarget(slot: string) {
	return insertion.target.value?.childId === props.child.id && insertion.target.value?.slotName === slot;
}

// Provide Injections

// We render another block that is not currently edited
if (isBlockSerialized(block.value)) {
	provideInBlockEditor(false);
}

provide(blockChildKey, () => props.child);

// Bounds

function useBounds() {
	const blockRef = shallowRef<Record<PropertyKey, any> | null>(null);
	const childBoundsElement = useBlockEditorChildBoundsElement(() => props.child.id);

	function onBlockRef(value: any) {
		blockRef.value = value;
	}

	const blockRootElement = computed(() => {
		if (childBoundsElement.value) {
			return childBoundsElement.value;
		}

		const ref = blockRef.value;

		// Resolve the block's root element across the supported emit shapes:
		// 1. direct component instance with explicit `[blockRefKey]` override (e.g. `Listing.block.vue`)
		// 2. wrapper component with `[blockRefKey]` override (serialized renderer path)
		// 3. wrapper component exposing `.blockRef` (serialized renderer path)
		// 4. raw component instance — `getBlockRootElement` -> `unrefElement` will pull `$el`
		return getBlockRootElement(
			ref?.[blockRefKey]
			?? ref?.blockRef?.[blockRefKey]
			?? ref?.blockRef
			?? ref,
		);
	});

	const computedBounds = useElementBounding(blockRootElement, {
		updateTiming: 'next-frame',
	});

	const randomBoundsId = useId();

	watch([
		computedBounds.x,
		computedBounds.y,
		computedBounds.width,
		computedBounds.height,
		() => props.slotName,
		() => props.index,
	], ([x, y, width, height, slotName, index], [oldX, oldY, oldWidth, oldHeight]) => {
		bounds.bounds.value.set(randomBoundsId, {
			x,
			y,
			width,
			height,
			childId: props.child.id,
			slotName,
			index,
			horizontal: horizontal.value,
		});

		if (oldX !== x || oldY !== y || oldWidth !== width || oldHeight !== height) {
			// Update positions of other blocks
			bounds.updateEvent.trigger(props.child.id);
		}
	}, { immediate: true });

	bounds.updateEvent.on((id) => {
		if (id !== props.child.id) {
			computedBounds.update();
		}
	});

	onBeforeUnmount(() => {
		bounds.bounds.value.delete(randomBoundsId);
	});

	return {
		onBlockRef,
	};
}

const { onBlockRef } = useBounds();

// Selection

const needSelectionForInteractive = false; // TODO configurable on block config

const { onBlockClick: _onBlockClick } = useHandleBlockClick();

function onBlockClick(event: MouseEvent, child: BlockSerializedChild) {
	if (needSelectionForInteractive) {
		_onBlockClick(event, child);
	}
}

const isInert = computed(() => {
	if (!needSelectionForInteractive) {
		return false;
	}

	for (const childId of selection.selectedChildIds.value) {
		let current: string | undefined = childId;

		while (current) {
			if (current === props.child.id) {
				return false;
			}

			current = getParent(current)?.id;
		}
	}

	return true;
});

// Suspend until the block is loaded

await until(() => block.value);
</script>

<template>
	<div
		v-if="hiddenByCanvasDrag"
		class="pointer-events-none overflow-hidden rounded-md bg-primary/5 opacity-30"
		:style="draggedCanvasPlaceholderStyle"
	/>

	<div
		v-else-if="block"
		class="contents"
		@click.stop="onBlockClick($event, child)"
	>
		<BlockRender
			:block="child.blockId"
			:props="propValues"
			:inert="isInert"
			:context="rootContext.getRootContextValues()"
			:private-context="{
				rootBlockOverride: editedBlock,
			}"
			unwrap-block-definition
			@block:load="insertion.clear()"
			@block:ref="onBlockRef"
		>
			<template
				v-for="(children, slot) of childrenBySlot"
				:key="slot"
				#[slot]
			>
				<!-- Will call `provideInBlockEditor(true)` -->
				<BlockEditorSlotWrapper
					:empty="children.length === 0"
				>
					<BlockEditorRenderChildren
						:children
						:slot-name="slot"
					>
						<template #before-child="{ index: childIndex }">
							<slot
								name="preview-block"
								:child
								:slot-name="slot"
								:index="childIndex"
							/>
						</template>

						<template #add-block="slotProps">
							<slot
								name="add-block"
								v-bind="slotProps"
							/>
						</template>

						<template #preview-block="slotProps">
							<slot
								name="preview-block"
								v-bind="slotProps"
							/>
						</template>

						<template #outlet="slotProps">
							<slot
								name="outlet"
								v-bind="slotProps"
							/>
						</template>
					</BlockEditorRenderChildren>

					<slot
						v-if="children.length === 0 && (slot === 'default' || selected || isInsertionTarget(slot))"
						name="add-block"
						:child
						:slot-name="slot"
						:index="children.length"
						:empty="true"
					/>

					<slot
						name="preview-block"
						:child
						:slot-name="slot"
						:index="children.length"
					/>
				</BlockEditorSlotWrapper>
			</template>
		</BlockRender>
	</div>

	<div v-else class="p-1 text-xs text-red-500">
		Block {{ props.child.blockId }} not found
	</div>
</template>
